// accept the content object and convert it into a single

import { pluralize } from "./pluralize";
import { groupBy, partition } from "lodash-es";
import {
  AssetGroupStatistics,
  AssetTypeInfo,
  AssetTypeStatistics,
} from "~/operations";

export type FormattableAssetGroup = Pick<
  AssetGroupStatistics,
  "groupType" | "statistics" | "listsAssetTypes" | "count"
>;

// `statistics` reports *concrete* asset types (e.g. "k8s.deployment",
// "aws.ec2_instance"), whereas an entry in `listsAssetTypes` may be an *alias*
// (e.g. "_alias.k8s_object_", "_alias.machine_instance_") whose `aliases` array
// lists the concrete types it aggregates. Sum the statistics counts for every
// concrete type the entry represents (its `aliases`, or itself when not an
// alias) so alias entries still resolve to a count.
const countForListType = (
  listType: AssetTypeInfo,
  statistics: AssetTypeStatistics[],
): number => {
  const concreteTypes = listType.aliases ?? [listType.assetType];
  return statistics
    .filter((data) => concreteTypes.includes(data.type.assetType))
    .reduce((total, data) => total + data.count, 0);
};

// accept the content object and convert it into a single
// comma separated string
export const formatGroupNames = (group: FormattableAssetGroup) => {
  if (!group.statistics) return;

  const { statistics } = group;
  if (group.groupType === "k8s") {
    const arr = group.listsAssetTypes
      .filter(
        (listType) =>
          listType.assetType === "k8s.cluster" ||
          listType.assetType === "_alias.k8s_object_",
      )
      .flatMap((listType) => {
        const count = countForListType(listType, statistics);
        return count > 0
          ? [`${count} ${pluralize(listType.shortName, count)}`]
          : [];
      });

    return arr.join(", ");
  }
  if (group.groupType === "aws") {
    // Split accounts and resources and get their total counts
    const [accounts, resources] = partition(
      Object.values(statistics)
        .filter((data) => data.count > 0)
        .filter((data) => data.type.assetType.startsWith("aws")),
      (data) => data.type.assetType === "aws.account",
    ).map((data) => data.reduce((t, c) => t + c.count, 0));

    // Format the string i.e. "1 account, 2 resources"
    const arr = [
      { count: accounts, title: "account" },
      { count: resources, title: "resource" },
    ]
      .flatMap((object) =>
        object.count > 0
          ? [`${object.count} ${pluralize(object.title, object.count)}`]
          : [],
      )
      .join(", ");

    return arr;
  }
  if (group.groupType === "iac") {
    return `${group.count} ${pluralize("asset", group.count)}`;
  }
  if (group.groupType === "gcp") {
    // Filter out empty counts and non-gcp assets
    const filteredStats = statistics
      .filter((data) => data.count > 0)
      .filter((data) => data.type.assetType.startsWith("gcp"));

    // Split accounts, organizations, and resources and get their total counts
    const groups = groupBy(filteredStats, (data) => {
      switch (data.type.assetType) {
        case "gcp.organization":
          return "org";
        case "gcp.project":
          return "project";
        default:
          return "resource";
      }
    });

    // Format the string i.e. "1 organization, 2 projects, 3 resources"
    return Object.entries(groups)
      .map(([key, value]) => ({
        count: value.reduce((t, c) => t + c.count, 0),
        title: key,
      }))
      .flatMap((object) =>
        object.count > 0
          ? [`${object.count} ${pluralize(object.title, object.count)}`]
          : [],
      )
      .join(", ");
  }

  if (group.groupType === "azure") {
    const includedAssetTypes = ["azure.subscription", "azure.compute_vm"];
    const objs = Object.values(statistics).filter((data) => data.count > 0);
    const arr = objs
      .filter((data) => includedAssetTypes.includes(data.type.assetType))
      .map(
        (data) => `${data.count} ${pluralize(data.type.shortName, data.count)}`,
      );
    const othersCount = objs
      .filter((data) => !includedAssetTypes.includes(data.type.assetType))
      .reduce((cur, data) => cur + data.count, 0);
    if (othersCount > 0) {
      arr.push(`${othersCount} ${pluralize("resource", othersCount)}`);
    }
    return arr.join(", ");
  }

  // Display one entry per asset type in `listsAssetTypes`, resolving alias
  // entries to the aggregate count of the concrete types they represent.
  const arr = group.listsAssetTypes.flatMap((listType) => {
    const count = countForListType(listType, statistics);
    return count > 0
      ? [`${count} ${pluralize(listType.shortName, count)}`]
      : [];
  });

  return arr.join(", ");
};
